import { supabase } from './client';

const QUERY_CACHE_TTL = 60_000; // 60 seconds
const _pointsTableCache = new Map<string, { data: PointsTableRow[]; ts: number }>();
const _playerStarsCache = new Map<string, { data: PlayerStarRow[]; ts: number }>();

export interface PointsTableRow {
  teamId: string;
  teamName: string;
  teamShortName: string;
  teamColor: string;
  teamLogoUrl: string | null;
  played: number;
  wins: number;
  losses: number;
  points: number;
  setsWon: number;
  setsLost: number;
  setsPlayed: number;
  setWinPct: number;
  pointsScored: number;
  pointsConceded: number;
  netPoints: number;
  position: number;
}

export async function getPointsTable(tournamentId: string): Promise<PointsTableRow[]> {
  const cached = _pointsTableCache.get(tournamentId);
  if (cached && Date.now() - cached.ts < QUERY_CACHE_TTL) return cached.data;
  try {
    // Fetch all completed league matches with their sets
    const { data: matches, error: mErr } = await supabase
      .from('matches')
      .select(`id, team_a_id, team_b_id, winner_id, match_type, status, sets(id, score_a, score_b, winner_id)`)
      .eq('tournament_id', tournamentId)
      .eq('match_type', 'league')
      .eq('status', 'match-won');
    if (mErr) {
      console.error('[getPointsTable] Match query error:', mErr);
      throw mErr;
    }

    // Fetch all teams for this tournament's matches
    const teamIds = new Set<string>();
    for (const m of (matches ?? [])) {
      teamIds.add(m.team_a_id);
      teamIds.add(m.team_b_id);
    }
    if (teamIds.size === 0) return [];

    const { data: teamsData, error: tErr } = await supabase
      .from('teams')
      .select('id, name, short_name, color, logo_url')
      .in('id', Array.from(teamIds));
    if (tErr) {
      console.error('[getPointsTable] Team query error:', tErr);
      throw tErr;
    }

  const teamMap = new Map<string, { name: string; shortName: string; color: string; logoUrl: string | null }>(
    (teamsData ?? []).map(t => [t.id, { name: t.name, shortName: t.short_name, color: t.color, logoUrl: t.logo_url ?? null }])
  );

  const stats = new Map<string, {
    played: number; wins: number; losses: number;
    setsWon: number; setsLost: number;
    pointsScored: number; pointsConceded: number;
  }>();

  const ensure = (id: string) => {
    if (!stats.has(id)) stats.set(id, { played: 0, wins: 0, losses: 0, setsWon: 0, setsLost: 0, pointsScored: 0, pointsConceded: 0 });
    return stats.get(id)!;
  };

  for (const m of (matches ?? [])) {
    const a = ensure(m.team_a_id);
    const b = ensure(m.team_b_id);
    a.played++;
    b.played++;
    if (m.winner_id === m.team_a_id) { a.wins++; b.losses++; }
    else if (m.winner_id === m.team_b_id) { b.wins++; a.losses++; }

    for (const s of (m.sets ?? [])) {
      a.setsWon += s.winner_id === m.team_a_id ? 1 : 0;
      a.setsLost += s.winner_id === m.team_b_id ? 1 : 0;
      b.setsWon += s.winner_id === m.team_b_id ? 1 : 0;
      b.setsLost += s.winner_id === m.team_a_id ? 1 : 0;
      a.pointsScored += s.score_a;
      a.pointsConceded += s.score_b;
      b.pointsScored += s.score_b;
      b.pointsConceded += s.score_a;
    }
  }

  const rows: Omit<PointsTableRow, 'position'>[] = Array.from(stats.entries()).map(([id, s]) => {
    const team = teamMap.get(id);
    const setsPlayed = s.setsWon + s.setsLost;
    return {
      teamId: id,
      teamName: team?.name ?? id,
      teamShortName: team?.shortName ?? id,
      teamColor: team?.color ?? '#888',
      teamLogoUrl: team?.logoUrl ?? null,
      played: s.played,
      wins: s.wins,
      losses: s.losses,
      points: s.wins * 2,
      setsWon: s.setsWon,
      setsLost: s.setsLost,
      setsPlayed,
      setWinPct: setsPlayed > 0 ? Math.round((s.setsWon / setsPlayed) * 1000) / 10 : 0,
      pointsScored: s.pointsScored,
      pointsConceded: s.pointsConceded,
      netPoints: s.pointsScored - s.pointsConceded,
    };
  });

  // Sort: 1) points desc, 2) setWinPct desc, 3) netPoints desc
  rows.sort((a, b) =>
    b.points - a.points ||
    b.setWinPct - a.setWinPct ||
    b.netPoints - a.netPoints
  );

  const result = rows.map((r, i) => ({ ...r, position: i + 1 }));
  _pointsTableCache.set(tournamentId, { data: result, ts: Date.now() });
  return result;
  } catch (err) {
    console.error('[getPointsTable] Failed to load points table:', err);
    throw err;
  }
}

export interface PlayerStarRow {
  playerId: string;
  playerName: string;
  teamId: string;
  teamName: string;
  teamShortName: string;
  teamColor: string;
  teamLogoUrl: string | null;
  throwerStars: number;
  acerStars: number;     // A - Acer
  // Volleyball category stars
  defenderStars: number; // D - Defender (from both teams)
  liberoStars: number;   // L - Libero (max 1 per team per rally)
  setterStars: number;   // S - Setter
  blockerStars: number;  // B - Blocker
  finisherStars: number; // F - Finisher
  totalStars: number;
}

export interface PlayerOfMatchWinner {
  playerId: string;
  playerName: string;
  teamId: string;
  teamName: string;
  totalStars: number;
  throwerStars: number;
  acerStars: number;
  defenderStars: number;
  liberoStars: number;
  setterStars: number;
  blockerStars: number;
  finisherStars: number;
}

/** Fetch ALL matches in a tournament with sets+rallies, for stats computation. */
async function getTournamentMatchesWithRallies(tournamentId: string) {
  console.log('[getTournamentMatchesWithRallies] Fetching matches for tournament:', tournamentId);
  const { data, error } = await supabase
    .from('matches')
    .select(`
      id, match_number, match_type, status, team_a_id, team_b_id, winner_id,
      sets(id, set_number, score_a, score_b, winner_id,
        rallies(id, rally_number, serving_team_id, server_id, point_won_by_id, lost_reason, point_winner_id, categories)
      )
    `)
    .eq('tournament_id', tournamentId)
    .order('match_number', { ascending: false });
  if (error) {
    console.error('[getTournamentMatchesWithRallies] Full error:', JSON.stringify(error, null, 2));
    console.error('[getTournamentMatchesWithRallies] Error code:', error.code);
    console.error('[getTournamentMatchesWithRallies] Error message:', error.message);
    console.error('[getTournamentMatchesWithRallies] Error details:', error.details);
    console.error('[getTournamentMatchesWithRallies] Error hint:', error.hint);
    throw error;
  }
  console.log('[getTournamentMatchesWithRallies] Found', data?.length ?? 0, 'matches');
  return data ?? [];
}

async function getTeamsForTournament(tournamentId: string) {
  const matches = await getTournamentMatchesWithRallies(tournamentId);
  const teamIds = new Set<string>();
  for (const m of matches) {
    teamIds.add(m.team_a_id);
    teamIds.add(m.team_b_id);
  }
  if (teamIds.size === 0) return { matches, teamMap: new Map() as Map<string, any>, playerMap: new Map() as Map<string, { id: string; name: string; teamId: string; }> };
  const { data: teamsData, error } = await supabase
    .from('teams')
    .select('id, name, short_name, color, logo_url, players(id, name, number, team_id)')
    .in('id', Array.from(teamIds));
  if (error) throw error;

  const teamMap = new Map<string, any>();
  const playerMap = new Map<string, { id: string; name: string; teamId: string; }>();
  for (const t of (teamsData ?? [])) {
    teamMap.set(t.id, t);
    for (const p of t.players) {
      playerMap.set(p.id, { id: p.id, name: p.name, teamId: t.id });
    }
  }
  return { matches, teamMap, playerMap };
}

/** Compute all player stars across a tournament. Includes all active matches (live, set-won, match-won). Excludes discarded only. */
export async function getPlayerStars(tournamentId: string): Promise<PlayerStarRow[]> {
  const cached = _playerStarsCache.get(tournamentId);
  if (cached && Date.now() - cached.ts < QUERY_CACHE_TTL) return cached.data;
  try {
    const { matches, teamMap, playerMap } = await getTeamsForTournament(tournamentId);
    if (playerMap.size === 0) return [];

    const stats = new Map<string, {
      thrower: number; acer: number; catcher: number;
      defender: number; libero: number; setter: number; blocker: number; finisher: number;
      teamId: string;
    }>();
    const ensure = (pid: string, tid: string) => {
      if (!stats.has(pid)) stats.set(pid, {
        thrower: 0, acer: 0, catcher: 0,
        defender: 0, libero: 0, setter: 0, blocker: 0, finisher: 0,
        teamId: tid
      });
      return stats.get(pid)!;
    };

    for (const m of matches) {
      // Skip discarded matches only
      if (m.status === 'discarded') continue;

      for (const s of m.sets ?? []) {
        for (const r of s.rallies ?? []) {
          // Acer Star — server delivers an ACE (from lost_reason)
          if (r.lost_reason === 'ACE') {
            const teamId = teamMap.get(r.serving_team_id)?.id ?? r.serving_team_id;
            const entry = ensure(r.server_id, teamId);
            entry.acer += 1;
          }
          // Point winner (player who scored the point)
          if (r.point_winner_id) {
            const teamOfWinner = teamMap.get(r.point_won_by_id);
            if (teamOfWinner) {
              const entry = ensure(r.point_winner_id, teamOfWinner.id);
              entry.thrower += 1;
            }
          }

          // Volleyball category stars from categories JSONB column
          if (r.categories && Array.isArray(r.categories)) {
            for (const cat of r.categories) {
              if (!cat.playerId || !cat.category) continue;
              const player = playerMap.get(cat.playerId);
              if (!player) continue;
              const entry = ensure(cat.playerId, player.teamId);

              switch (cat.category) {
                case 'A': entry.acer += 1; break;
                case 'D': entry.defender += 1; break;
                case 'L': entry.libero += 1; break;
                case 'S': entry.setter += 1; break;
                case 'B': entry.blocker += 1; break;
                case 'F': entry.finisher += 1; break;
              }
            }
          }
        }
      }
    }

    const rows: PlayerStarRow[] = Array.from(stats.entries()).map(([pid, s]) => {
      const player = playerMap.get(pid);
      const team = teamMap.get(s.teamId);
      const volleyballTotal = s.defender + s.libero + s.setter + s.blocker + s.finisher;
      const legacyTotal = s.thrower + s.acer;
      return {
        playerId: pid,
        playerName: player?.name ?? pid,
        teamId: s.teamId,
        teamName: team?.name ?? s.teamId,
        teamShortName: team?.short_name ?? s.teamId,
        teamColor: team?.color ?? '#888',
        teamLogoUrl: team?.logo_url ?? null,
        throwerStars: s.thrower,
        acerStars: s.acer,
        defenderStars: s.defender,
        liberoStars: s.libero,
        setterStars: s.setter,
        blockerStars: s.blocker,
        finisherStars: s.finisher,
        totalStars: Math.max(legacyTotal, volleyballTotal + s.acer),
      };
    });

    _playerStarsCache.set(tournamentId, { data: rows, ts: Date.now() });
    return rows;
  } catch (err) {
    console.error('[getPlayerStars] Failed to load statistics:', err);
    throw err;
  }
}

/** Compute Player Of The Match for a single match. Eligible: winning team only. Highest total match stars wins (ties allowed). */
export async function getPlayerOfMatch(matchId: string): Promise<PlayerOfMatchWinner[]> {
  console.log('[PlayerOfMatch] matchId=', matchId);

  const { data, error } = await supabase
    .from('matches')
    .select(`
      id, team_a_id, team_b_id, winner_id, status,
      sets(id, rallies(id, server_id, point_won_by_id, lost_reason, serving_team_id, point_winner_id, categories))
    `)
    .eq('id', matchId)
    .single();
  if (error) {
    console.error('[PlayerOfMatch] DB fetch error:', error);
    throw error;
  }
  if (!data) {
    console.log('[PlayerOfMatch] No match data returned');
    return [];
  }
  if (data.status !== 'match-won') {
    console.log('[PlayerOfMatch] Match status is not match-won, status=', data.status);
    return [];
  }
  if (!data.winner_id) {
    console.log('[PlayerOfMatch] No winner_id on match');
    return [];
  }

  console.log('[PlayerOfMatch] winningTeam=', data.winner_id, '(teamA=', data.team_a_id, ', teamB=', data.team_b_id, ')');

  const teamIds = [data.team_a_id, data.team_b_id];
  const { data: teamsData, error: tErr } = await supabase
    .from('teams')
    .select('id, name, short_name, players(id, name, team_id)')
    .in('id', teamIds);
  if (tErr) {
    console.error('[PlayerOfMatch] Teams fetch error:', tErr);
    throw tErr;
  }

  const winningTeam = teamsData?.find(t => t.id === data.winner_id);
  if (!winningTeam) {
    console.log('[PlayerOfMatch] winningTeam not found in teamsData');
    return [];
  }

  console.log('[PlayerOfMatch] candidatePlayers=', winningTeam.players.map((p: any) => p.name));

  // Compute stars per player (all players, filter winners later)
  const playerStars = new Map<string, { thrower: number; acer: number; defender: number; libero: number; setter: number; blocker: number; finisher: number; }>();
  const ensure = (pid: string) => {
    if (!playerStars.has(pid)) playerStars.set(pid, { thrower: 0, acer: 0, defender: 0, libero: 0, setter: 0, blocker: 0, finisher: 0 });
    return playerStars.get(pid)!;
  };

  let totalRallies = 0;
  for (const s of data.sets) {
    for (const r of s.rallies) {
      totalRallies++;
      if (r.lost_reason === 'ACE' && r.server_id) {
        ensure(r.server_id).acer += 1;
      }
      if (r.point_winner_id) {
        ensure(r.point_winner_id).thrower += 1;
      }
      // Volleyball category stars
      if (r.categories && Array.isArray(r.categories)) {
        for (const cat of r.categories) {
          if (!cat.playerId || !cat.category) continue;
          const entry = ensure(cat.playerId);
          switch (cat.category) {
            case 'A': entry.acer += 1; break;
            case 'D': entry.defender += 1; break;
            case 'L': entry.libero += 1; break;
            case 'S': entry.setter += 1; break;
            case 'B': entry.blocker += 1; break;
            case 'F': entry.finisher += 1; break;
          }
        }
      }
    }
  }
  console.log('[PlayerOfMatch] totalRallies=', totalRallies, 'sets=', data.sets.length);

  // Log per-player star breakdown for winning team
  for (const p of winningTeam.players as any[]) {
    const s = playerStars.get(p.id);
    const total = (s?.thrower ?? 0) + (s?.acer ?? 0) + (s?.defender ?? 0) + (s?.libero ?? 0) + (s?.setter ?? 0) + (s?.blocker ?? 0) + (s?.finisher ?? 0);
    console.log(`[PlayerOfMatch] player=${p.name} total=${total}`);
  }

  // Filter to winning team players only
  const winningPlayers = winningTeam.players as any[];
  let maxStars = 0;
  for (const p of winningPlayers) {
    const s = playerStars.get(p.id);
    if (!s) continue;
    const total = s.thrower + s.acer + s.defender + s.libero + s.setter + s.blocker + s.finisher;
    if (total > maxStars) maxStars = total;
  }

  console.log('[PlayerOfMatch] maxStars=', maxStars);

  if (maxStars === 0) {
    console.log('[PlayerOfMatch] maxStars=0, no player of match (no stars recorded for winning team)');
    return [];
  }

  const winners = winningPlayers
    .filter(p => {
      const s = playerStars.get(p.id);
      if (!s) return false;
      const total = s.thrower + s.acer + s.defender + s.libero + s.setter + s.blocker + s.finisher;
      return total === maxStars;
    })
    .map(p => {
      const s = playerStars.get(p.id)!;
      return {
        playerId: p.id,
        playerName: p.name,
        teamId: winningTeam.id,
        teamName: winningTeam.name,
        totalStars: maxStars,
        throwerStars: s.thrower,
        acerStars: s.acer,
        defenderStars: s.defender,
        liberoStars: s.libero,
        setterStars: s.setter,
        blockerStars: s.blocker,
        finisherStars: s.finisher,
      };
    });

  console.log('[PlayerOfMatch] result=', winners.map(w => `${w.playerName} (${w.totalStars} stars)`));

  // Persist Player of Match to the match record
  const winnerIds = winners.map(w => w.playerId);
  await supabase
    .from('matches')
    .update({
      player_of_match_ids: winnerIds,
      player_of_match_team_id: winningTeam.id,
    })
    .eq('id', matchId);

  return winners;
}

/** Save Player of Match result directly (used after match completion). */
export async function savePlayerOfMatch(matchId: string, playerIds: string[], teamId: string): Promise<void> {
  const { error } = await supabase
    .from('matches')
    .update({
      player_of_match_ids: playerIds,
      player_of_match_team_id: teamId,
    })
    .eq('id', matchId);
  if (error) throw error;
}

/** Normalizes a player's display name into a URL-safe slug, e.g. "Nambi Raj" -> "nambi-raj". */
export function slugifyPlayerName(name: string): string {
  return name
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
}

export interface PlayerCareerTeamStint {
  teamId: string;
  teamName: string;
  teamShortName: string;
  teamColor: string;
  teamLogoUrl: string | null;
  jerseyNumbers: number[];
  matchesPlayed: number;
}

export interface PlayerCareerMatch {
  matchId: string;
  matchNumber: number;
  date: string | null;
  tournamentId: string | null;
  teamShortName: string;
  teamColor: string;
  opponentShortName: string;
  opponentColor: string;
  result: 'W' | 'L' | '-';
  starsInMatch: number;
}

export interface PlayerCareerProfile {
  slug: string;
  displayName: string;
  playerIds: string[];
  teams: PlayerCareerTeamStint[];
  totalMatches: number;
  wins: number;
  losses: number;
  playerOfMatchCount: number;
  stats: {
    totalStars: number;
    throwerStars: number;
    acerStars: number;
    defenderStars: number;
    liberoStars: number;
    setterStars: number;
    blockerStars: number;
    finisherStars: number;
  };
  recentMatches: PlayerCareerMatch[];
}

/**
 * Fetches a player's career stats aggregated across every team/tournament they've played for.
 * Players are matched by slugified name (case-insensitive), not by DB id, since a team
 * change creates a brand-new `players` row with no link back to the old one. Any two
 * `players` rows sharing a name are merged into one profile.
 */
export async function getPlayerProfileBySlug(slug: string): Promise<PlayerCareerProfile | null> {
  const { data: allPlayers, error: playersErr } = await supabase
    .from('players')
    .select('id, name, number, team_id, team:teams(id, name, short_name, color, logo_url)');
  if (playersErr) throw playersErr;
  if (!allPlayers) return null;

  const matchingPlayers = (allPlayers as any[]).filter(p => slugifyPlayerName(p.name) === slug);
  if (matchingPlayers.length === 0) return null;

  const playerIds = matchingPlayers.map(p => p.id);
  const displayName = matchingPlayers[0].name;

  const teamMap = new Map<string, PlayerCareerTeamStint>();
  for (const p of matchingPlayers) {
    const t = p.team;
    if (!t) continue;
    if (!teamMap.has(t.id)) {
      teamMap.set(t.id, {
        teamId: t.id,
        teamName: t.name,
        teamShortName: t.short_name,
        teamColor: t.color,
        teamLogoUrl: t.logo_url,
        jerseyNumbers: [],
        matchesPlayed: 0,
      });
    }
    const entry = teamMap.get(t.id)!;
    if (!entry.jerseyNumbers.includes(p.number)) entry.jerseyNumbers.push(p.number);
  }
  const teamIds = Array.from(teamMap.keys());

  const emptyProfile: PlayerCareerProfile = {
    slug,
    displayName,
    playerIds,
    teams: Array.from(teamMap.values()),
    totalMatches: 0,
    wins: 0,
    losses: 0,
    playerOfMatchCount: 0,
    stats: {
      totalStars: 0, throwerStars: 0, acerStars: 0,
      defenderStars: 0, liberoStars: 0, setterStars: 0, blockerStars: 0, finisherStars: 0,
    },
    recentMatches: [],
  };
  if (teamIds.length === 0) return emptyProfile;

  const orFilter = teamIds.flatMap(id => [`team_a_id.eq.${id}`, `team_b_id.eq.${id}`]).join(',');
  const { data: matchRows, error: matchErr } = await supabase
    .from('matches')
    .select(`
      id, match_number, date, match_type, status, team_a_id, team_b_id, winner_id, tournament_id, player_of_match_ids,
      sets(id, rallies(id, server_id, point_won_by_id, point_winner_id, lost_reason, serving_team_id, categories))
    `)
    .or(orFilter)
    .neq('status', 'discarded')
    .order('match_number', { ascending: false });
  if (matchErr) throw matchErr;
  if (!matchRows || matchRows.length === 0) return emptyProfile;

  // Opponent teams aren't in teamMap yet — fetch their display info in one batch.
  const opponentTeamIds = new Set<string>();
  for (const m of matchRows) {
    if (!teamIds.includes(m.team_a_id)) opponentTeamIds.add(m.team_a_id);
    if (!teamIds.includes(m.team_b_id)) opponentTeamIds.add(m.team_b_id);
  }
  const opponentTeamMap = new Map<string, { shortName: string; color: string }>();
  if (opponentTeamIds.size > 0) {
    const { data: opponentTeams, error: oppErr } = await supabase
      .from('teams')
      .select('id, short_name, color')
      .in('id', Array.from(opponentTeamIds));
    if (oppErr) throw oppErr;
    for (const t of opponentTeams ?? []) {
      opponentTeamMap.set(t.id, { shortName: t.short_name, color: t.color });
    }
  }

  let throwerTotal = 0, acerTotal = 0, defenderTotal = 0, liberoTotal = 0, setterTotal = 0, blockerTotal = 0, finisherTotal = 0;
  let wins = 0, losses = 0, playerOfMatchCount = 0, totalMatchesCount = 0;
  const recentMatches: PlayerCareerMatch[] = [];

  for (const m of matchRows) {
    const myTeamId = teamIds.includes(m.team_a_id) ? m.team_a_id : (teamIds.includes(m.team_b_id) ? m.team_b_id : null);
    if (!myTeamId) continue;
    const teamEntry = teamMap.get(myTeamId);

    let starsInMatch = 0;
    let participated = false;

    for (const s of m.sets ?? []) {
      for (const r of s.rallies ?? []) {
        if (r.lost_reason === 'ACE' && playerIds.includes(r.server_id)) {
          acerTotal += 1;
          starsInMatch += 1;
          participated = true;
        }
        if (r.point_winner_id && playerIds.includes(r.point_winner_id)) {
          throwerTotal += 1;
          starsInMatch += 1;
          participated = true;
        }
        if (r.categories && Array.isArray(r.categories)) {
          for (const cat of r.categories) {
            if (!cat.playerId || !cat.category || !playerIds.includes(cat.playerId)) continue;
            participated = true;
            switch (cat.category) {
              case 'A': acerTotal += 1; starsInMatch += 1; break;
              case 'D': defenderTotal += 1; starsInMatch += 1; break;
              case 'L': liberoTotal += 1; starsInMatch += 1; break;
              case 'S': setterTotal += 1; starsInMatch += 1; break;
              case 'B': blockerTotal += 1; starsInMatch += 1; break;
              case 'F': finisherTotal += 1; starsInMatch += 1; break;
            }
          }
        }
      }
    }

    if (!participated) continue;

    totalMatchesCount += 1;
    if (teamEntry) teamEntry.matchesPlayed += 1;

    const isWin = m.status === 'match-won' && m.winner_id === myTeamId;
    const isLoss = m.status === 'match-won' && !!m.winner_id && m.winner_id !== myTeamId;
    if (isWin) wins += 1;
    if (isLoss) losses += 1;

    if (m.player_of_match_ids && playerIds.some(pid => (m.player_of_match_ids as string[]).includes(pid))) {
      playerOfMatchCount += 1;
    }

    const opponentTeamId = m.team_a_id === myTeamId ? m.team_b_id : m.team_a_id;
    const opponent = teamMap.get(opponentTeamId) ?? opponentTeamMap.get(opponentTeamId);
    recentMatches.push({
      matchId: m.id,
      matchNumber: m.match_number,
      date: m.date,
      tournamentId: m.tournament_id,
      teamShortName: teamEntry?.teamShortName ?? '',
      teamColor: teamEntry?.teamColor ?? '#888',
      opponentShortName: (opponent as any)?.teamShortName ?? (opponent as any)?.shortName ?? '—',
      opponentColor: (opponent as any)?.teamColor ?? (opponent as any)?.color ?? '#888',
      result: isWin ? 'W' : isLoss ? 'L' : '-',
      starsInMatch,
    });
  }

  const legacyTotal = throwerTotal + acerTotal;
  const volleyballTotal = defenderTotal + liberoTotal + setterTotal + blockerTotal + finisherTotal;
  const totalStars = Math.max(legacyTotal, volleyballTotal + acerTotal);

  return {
    slug,
    displayName,
    playerIds,
    teams: Array.from(teamMap.values()),
    totalMatches: totalMatchesCount,
    wins,
    losses,
    playerOfMatchCount,
    stats: {
      totalStars,
      throwerStars: throwerTotal,
      acerStars: acerTotal,
      defenderStars: defenderTotal,
      liberoStars: liberoTotal,
      setterStars: setterTotal,
      blockerStars: blockerTotal,
      finisherStars: finisherTotal,
    },
    recentMatches,
  };
}
